import { create } from 'zustand';

export interface OpenFile {
  path: string;
  content: string;
  language: string;
  isDirty: boolean;
}

interface IDEState {
  mode: 'code' | 'visual';
  activeFile: string | null;
  openFiles: OpenFile[];
  panelVisibility: {
    fileExplorer: boolean;
    terminal: boolean;
    preview: boolean;
    aiChat: boolean;
  };

  // Actions
  setMode: (mode: 'code' | 'visual') => void;
  openFile: (path: string, content: string, language: string) => void;
  closeFile: (path: string) => void;
  setActiveFile: (path: string) => void;
  updateFileContent: (path: string, content: string) => void;
  markFileSaved: (path: string) => void;
  togglePanel: (panel: keyof IDEState['panelVisibility']) => void;
}

export const useIDEStore = create<IDEState>((set, get) => ({
  mode: 'code',
  activeFile: null,
  openFiles: [],
  panelVisibility: {
    fileExplorer: true,
    terminal: true,
    preview: true,
    aiChat: true,
  },

  setMode: (mode) => set({ mode }),

  openFile: (path, content, language) => {
    const { openFiles } = get();
    const exists = openFiles.find((f) => f.path === path);
    if (!exists) {
      set({
        openFiles: [...openFiles, { path, content, language, isDirty: false }],
        activeFile: path,
      });
    } else {
      // Update content if file is already open and not dirty (external change)
      if (!exists.isDirty && exists.content !== content) {
        set({
          openFiles: openFiles.map((f) =>
            f.path === path ? { ...f, content, language } : f
          ),
          activeFile: path,
        });
      } else {
        set({ activeFile: path });
      }
    }
  },

  closeFile: (path) => {
    const { openFiles, activeFile } = get();
    const closingIndex = openFiles.findIndex((f) => f.path === path);
    const filtered = openFiles.filter((f) => f.path !== path);
    let newActive = activeFile;
    if (activeFile === path) {
      // Prefer the tab to the right, then left, then null
      if (filtered.length === 0) {
        newActive = null;
      } else if (closingIndex < filtered.length) {
        newActive = filtered[closingIndex].path;
      } else {
        newActive = filtered[filtered.length - 1].path;
      }
    }
    set({ openFiles: filtered, activeFile: newActive });
  },

  setActiveFile: (path) => set({ activeFile: path }),

  updateFileContent: (path, content) => {
    set((state) => ({
      openFiles: state.openFiles.map((f) =>
        f.path === path ? { ...f, content, isDirty: true } : f
      ),
    }));
  },

  markFileSaved: (path) => {
    set((state) => ({
      openFiles: state.openFiles.map((f) =>
        f.path === path ? { ...f, isDirty: false } : f
      ),
    }));
  },

  togglePanel: (panel) => {
    set((state) => ({
      panelVisibility: {
        ...state.panelVisibility,
        [panel]: !state.panelVisibility[panel],
      },
    }));
  },
}));
